import { useState, useContext } from 'react';
import { AppContext } from '@/context/AppContext';
import { FileDown } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { generateFilename } from '@/utils/pdfUtils';
import LoadingSpinner from '@/components/shared/LoadingSpinner';

export default function PDFExport() {
  const { activeClient, rankings, activeClientId, addToast } = useContext(AppContext);
  const [isGenerating, setIsGenerating] = useState(false);

  const clientRankings = rankings[activeClientId] || [];
  const hasRankings = clientRankings.length > 0;

  const generatePDF = async () => {
    setIsGenerating(true);
    const reportElement = document.getElementById('pdf-report');

    if (!reportElement) {
      addToast('PDF report element not found', 'error');
      setIsGenerating(false);
      return;
    }

    try {
      // Make visible for capture
      reportElement.style.visibility = 'visible';
      reportElement.style.position = 'fixed';
      reportElement.style.top = '0';
      reportElement.style.left = '0';
      reportElement.style.width = '800px';
      reportElement.style.zIndex = '-1';

      await new Promise(r => setTimeout(r, 300));

      const sections = reportElement.querySelectorAll('.pdf-page');
      const doc = new jsPDF({
        format: 'a4',
        orientation: 'portrait',
        unit: 'px',
      });

      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];
        const canvas = await html2canvas(section, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#0A0F1E',
          logging: false,
        });

        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        const pdfW = doc.internal.pageSize.getWidth();
        const pdfH = (canvas.height * pdfW) / canvas.width;

        if (i > 0) doc.addPage();
        doc.addImage(imgData, 'JPEG', 0, 0, pdfW, pdfH);
      }

      const filename = generateFilename(activeClient?.name || 'client');
      doc.save(filename);
      addToast('PDF exported successfully!', 'success');
    } catch (error) {
      console.error('Error generating PDF:', error);
      addToast('Failed to generate PDF', 'error');
    } finally {
      // Hide again
      reportElement.style.visibility = 'hidden';
      reportElement.style.position = '';
      reportElement.style.top = '';
      reportElement.style.left = '';
      reportElement.style.width = '';
      reportElement.style.zIndex = '';
      setIsGenerating(false);
    }
  };

  return (
    <button
      onClick={generatePDF}
      disabled={isGenerating || !hasRankings}
      className="button-ghost text-xs sm:text-sm flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 disabled:opacity-50 disabled:cursor-not-allowed"
      data-testid="export-pdf-btn"
    >
      {isGenerating ? (
        <>
          <LoadingSpinner size="sm" />
          <span className="hidden sm:inline">Generating...</span>
        </>
      ) : (
        <>
          <FileDown size={14} className="sm:w-4 sm:h-4" />
          <span className="hidden xs:inline">PDF</span>
        </>
      )}
    </button>
  );
}
